import React, { useState, useEffect } from "react";
import MkdSDK from "@/utils/MkdSDK";
import { useNavigate, useParams } from "react-router-dom";
import { tokenExpireError } from "@/context/Auth";
import { GlobalContext, showToast } from "@/context/Global";
import { AuthContext } from "@/context/Auth";
import { LazyLoad } from "@/components/LazyLoad";
import { InteractiveButton } from "@/components/InteractiveButton";
import { ArrowLeft, Calendar, CreditCard, DollarSign, Tag } from "lucide-react";
import { formatCurrency } from "@/utils/utils";

interface PriceData {
  id: string;
  name: string;
  amount: number;
  currency: string;
  type: string;
  interval?: string;
  interval_count?: number;
  trial_days: number;
  is_usage_metered: boolean;
  usage_limit?: number;
  pay_per_usage: boolean;
  pay_per_seat: boolean;
  active: boolean;
  status: number;
  created_at: string;
}

const ViewAdminPriceDetailPage: React.FC = () => {
  const { dispatch: globalDispatch } = React.useContext(GlobalContext);
  const { dispatch: authDispatch } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const params = useParams();
  const [price, setPrice] = useState<PriceData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPriceData = async () => {
      setLoading(true);
      try {
        const sdk = new MkdSDK();
        const result = await sdk.callRawAPI(
          `/v1/api/contentgap/customer/lambda/stripe/price/${params.id}`,
          {},
          "GET"
        );

        if (!result.error) {
          setPrice(result.model);
        } else {
          setError("Failed to load price data");
          showToast(globalDispatch, "Failed to load price data", 5000, "error");
        }
      } catch (error: any) {
        console.log("error", error);
        setError("An error occurred while fetching price data");
        tokenExpireError(authDispatch, error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPriceData();
  }, [authDispatch, params?.id, globalDispatch]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getBillingText = (price: PriceData) => {
    if (price.type === "one_time") {
      return "One-time payment";
    }

    const interval =
      price.interval_count === 1
        ? price.interval
        : `${price.interval_count} ${price.interval}s`;

    return `Every ${interval}`;
  };

  // Format price with currency
  const formatPrice = (amount: number, currency: string) => {
    return formatCurrency(amount / 100, currency || "USD");
  };

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !price) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4">
        <p className="text-red-500">{error || "Price not found"}</p>
        <InteractiveButton onClick={() => navigate("/admin/prices")}>
          Back to Prices
        </InteractiveButton>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-auto p-6">
      <div className="mb-6 flex items-center">
        <InteractiveButton
          onClick={() => navigate("/admin/prices")}
          className="mr-4 !bg-transparent !p-2 !text-gray-600 hover:!bg-gray-100"
        >
          <ArrowLeft size={20} />
        </InteractiveButton>
        <h1 className="text-2xl font-bold text-gray-800">Price Details</h1>
      </div>

      <div className="rounded-lg bg-white p-6 shadow-md">
        <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-500">
              Price Name
            </span>
            <span className="text-lg font-semibold">{price.name}</span>
          </div>

          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-500">Status</span>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                price.status === 1
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {price.status === 1 ? "Active" : "Inactive"}
            </span>
          </div>

          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-500">Amount</span>
            <div className="flex items-center">
              <DollarSign className="mr-1 h-5 w-5 text-green-600" />
              <span className="text-lg font-semibold">
                {formatPrice(price.amount, price.currency)}
              </span>
            </div>
          </div>

          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-500">
              Billing Type
            </span>
            <div className="flex items-center">
              <CreditCard className="mr-1 h-5 w-5 text-blue-600" />
              <span className="text-lg font-semibold">
                {price.type === "recurring" ? "Recurring" : "One-time"}
              </span>
            </div>
          </div>

          {/* {price.type === "recurring" && (
            <div className="flex flex-col">
              <span className="text-sm font-medium text-gray-500">
                Billing Interval
              </span>
              <div className="flex items-center">
                <Calendar className="mr-1 h-5 w-5 text-purple-600" />
                <span className="text-lg font-semibold">
                  {getBillingText(price)}
                </span>
              </div>
            </div>
          )} */}

          {/* <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-500">
              Trial Period
            </span>
            <span className="text-lg font-semibold">
              {price.trial_days > 0 ? `${price.trial_days} days` : "No trial"}
            </span>
          </div> */}

          {/* <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-500">
              Usage Type
            </span>
            <div className="flex items-center">
              <Tag className="mr-1 h-5 w-5 text-orange-600" />
              <span className="text-lg font-semibold">
                {price.is_usage_metered ? "Metered" : "Unmetered"}
              </span>
            </div>
          </div> */}

          {/* {price.is_usage_metered && price.usage_limit && (
            <div className="flex flex-col">
              <span className="text-sm font-medium text-gray-500">
                Usage Limit
              </span>
              <span className="text-lg font-semibold">
                {price.usage_limit} units
              </span>
            </div>
          )} */}

          {/* <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-500">
              Pay Per Usage
            </span>
            <span className="text-lg font-semibold">
              {price.pay_per_usage ? "Yes" : "No"}
            </span>
          </div>

          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-500">
              Pay Per Seat
            </span>
            <span className="text-lg font-semibold">
              {price.pay_per_seat ? "Yes" : "No"}
            </span>
          </div> */}

          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-500">
              Created On
            </span>
            <span className="text-lg font-semibold">
              {formatDate(price.created_at)}
            </span>
          </div>
        </div>

        <div className="mt-8 flex justify-end space-x-4">
          <InteractiveButton
            onClick={() => navigate(`/admin/prices/edit/${price.id}`)}
            className="!bg-blue-600 hover:!bg-blue-700"
          >
            Edit Price
          </InteractiveButton>
        </div>
      </div>
    </div>
  );
};

export default ViewAdminPriceDetailPage;
